import React, { Component } from 'react';
import { flowRight as compose } from 'lodash';
import { withQuery, withMutation } from '../../../hoc/withGraphql';
import getSentNotifications from './getSentNotifications.graphql';
import sendNotificationM from './sendNotification.graphql';

const RED = '#D32F2F';
const INK = '#1F2733';
const TEAL = '#0E9488';

// Program N (N2): admin Notification Center — compose + send a persistent notification to an
// audience, and a history table with delivery + read counts. Pairs with the besthand-api inbox
// (bell + read state in the apps).
const AUDIENCES = [
  { v: 'all', l: 'Everyone (clients + workers)' },
  { v: 'all_clients', l: 'All clients' },
  { v: 'all_workers', l: 'All workers' },
  { v: 'user', l: 'A single user (by ID)' },
];

const TYPES = [
  { v: 'general', l: 'General' },
  { v: 'promo', l: 'Promo' },
  { v: 'system', l: 'System' },
];

class NotificationCenter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      busy: false, message: '',
      title: '', body: '', type: 'general', audience: 'all', targetUserId: '', deepLink: '',
    };
  }

  send = async () => {
    const { title, body, type, audience, targetUserId, deepLink } = this.state;
    if (!title || !body) { this.setState({ message: 'Title and message are required.' }); return; }
    if (audience === 'user' && !targetUserId) { this.setState({ message: 'Enter the target user ID.' }); return; }
    const aud = AUDIENCES.find((a) => a.v === audience);
    if (!window.confirm(`Send "${title}" to ${aud ? aud.l : audience}? This sends a push notification and adds it to their inbox.`)) return;
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.sendNotificationMut({
        variables: { title, body, type, audience, targetUserId: audience === 'user' ? targetUserId : null, deepLink: deepLink || null },
      });
      const r = res && res.data && res.data.sendNotification;
      if (r && r.status === 200) {
        this.setState({ busy: false, message: `✅ Sent to ${r.recipientCount} recipient(s).`, title: '', body: '', targetUserId: '', deepLink: '' });
        this.props.sent.refetch();
      } else {
        this.setState({ busy: false, message: (r && r.errorMessage) || 'Send failed.' });
      }
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  render() {
    const input = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginRight: 8 };
    const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginRight: 8 });
    const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 16 };
    const th = { padding: 10, textAlign: 'left' };
    const td = { padding: 10, borderTop: '1px solid #f0f0f0', fontSize: 13 };

    const sent = (this.props.sent && this.props.sent.getSentNotifications && this.props.sent.getSentNotifications.result) || [];
    const audienceLabel = (v) => (AUDIENCES.find((a) => a.v === v) || {}).l || v;

    return (
      <div style={{ fontFamily: 'Poppins, sans-serif' }}>
        <h1 className="siteAdminPrimaryHeading">Notifications</h1>
        {this.state.message ? <div style={{ marginBottom: 12, color: this.state.message[0] === '✅' ? TEAL : RED, fontWeight: 600 }}>{this.state.message}</div> : null}

        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>New notification</div>

          <div style={{ marginBottom: 8 }}>
            <select style={input} value={this.state.audience} onChange={(e) => this.setState({ audience: e.target.value })}>
              {AUDIENCES.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
            </select>
            <select style={input} value={this.state.type} onChange={(e) => this.setState({ type: e.target.value })}>
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            {this.state.audience === 'user' && (
              <input style={{ ...input, width: 320 }} placeholder="Target user ID (UUID)" value={this.state.targetUserId}
                     onChange={(e) => this.setState({ targetUserId: e.target.value.trim() })} />
            )}
          </div>

          <input style={{ ...input, width: 480, display: 'block', marginBottom: 8 }} placeholder="Title" value={this.state.title}
                 onChange={(e) => this.setState({ title: e.target.value })} />
          <textarea style={{ ...input, width: 600, height: 120, display: 'block', marginBottom: 8 }} placeholder="Message…" value={this.state.body}
                    onChange={(e) => this.setState({ body: e.target.value })} />
          <input style={{ ...input, width: 320, display: 'block', marginBottom: 10 }} placeholder="Deep link (optional, e.g. /bookings/123)" value={this.state.deepLink}
                 onChange={(e) => this.setState({ deepLink: e.target.value.trim() })} />

          <button style={btn(RED)} disabled={this.state.busy} onClick={this.send}>{this.state.busy ? 'Sending…' : 'Send notification'}</button>
          <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Sends a push notification AND saves it to each recipient&apos;s inbox (stays readable, no longer disappears).</div>
        </div>

        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', fontWeight: 700, borderBottom: '1px solid #f0f0f0' }}>Sent history</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#fafafa' }}>
              <th style={th}>Date</th><th style={th}>Audience</th><th style={th}>Type</th><th style={th}>Title</th>
              <th style={th}>Recipients</th><th style={th}>Read</th>
            </tr></thead>
            <tbody>
              {sent.length === 0 && <tr><td style={td} colSpan={6}>No notifications sent yet.</td></tr>}
              {sent.map((n) => (
                <tr key={n.id}>
                  <td style={td}>{n.createdAt ? n.createdAt.slice(0, 10) : '—'}</td>
                  <td style={td}>{audienceLabel(n.audience)}{n.audience === 'user' && n.targetUserId ? ` (${n.targetUserId.slice(0, 8)}…)` : ''}</td>
                  <td style={td}>{n.type}</td>
                  <td style={td} title={n.body}>{n.title}</td>
                  <td style={td}>{n.recipientCount}</td>
                  <td style={td}>{n.readCount} / {n.recipientCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

export default compose(
  withQuery(getSentNotifications, { name: 'sent', options: { variables: { limit: 50, offset: 0 }, fetchPolicy: 'network-only', ssr: false } }),
  withMutation(sendNotificationM, { name: 'sendNotificationMut' }),
)(NotificationCenter);
